import './underwriting.quote.grid.scss';

import {
  Box,
  Checkbox,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ConfirmDialog,
  ConvergeTooltip,
  NewConvergeButton,
  PageTitle,
  PaperLayout,
  PrimaryTitle,
  StatusBadge,
  TableActionButton,
} from '../../../presentation';
import { CustomPaletteColor, CustomPaletteColorType } from '../../../theme';
import {
  Dispatch,
  Fragment,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  GET_QUOTES,
  QuoteDetails,
  QuoteStatus,
  SubmissionInfo,
  SubmissionInput,
  SubmissionStatus,
  SubmissionSubStatus,
  SubmissionType,
  UpsertQuoteMutationVariables,
  getPaginatedNodes,
  useBindQuoteMutation,
  useCloneQuoteMutation,
  useDeleteQuoteMutation,
  useUpsertQuoteMutation,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import {
  IExcessQuoteLineItem,
  IQuoteLineItemGroup,
  QuoteLineItem,
  QuoteLineItemType,
  useQuoteListActionsContext,
  useQuoteListDispatchContext,
  useQuoteListStateContext,
} from './quote-list.context';
import { Sync, Warning } from '@mui/icons-material';
import {
  UnderwritingSubmissionDetailPageSlug,
  isConverge,
  toTitleCase,
} from '@converge/data';
import {
  combineQuoteLineItems,
  combineQuoteLineItemsIntoGroups,
  flattenQuoteLineItems,
} from './utils';
import {
  displayFormatDate,
  displayNumber,
  formatDollarAmount,
} from '@converge/utils';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ErrorPageLayout } from '../../../presentation';
import { RaterMode } from '@converge/graphql';
import { RaterQuoteName } from '../rater/header';
import { Stack } from '@mui/system';
import { UnderwritingQuoteCreateButton } from './underwriting.quote.create.button';
import { UnderwritingSortQuoteOptions } from './underwriting.quote.sort.options';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { underwritingQuoteToUpsertVariables } from './utils';
import { useConvergeSnackBar } from '../../../contexts';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

interface IUnderwritingQuoteGridProps {
  submissionUuid: string;
  submissionType: SubmissionType;
}

export interface IUnderwritingQuoteBindQuote {
  quoteInput: UpsertQuoteMutationVariables;
  submissionDetails: SubmissionInput;
  shouldUpsertQuote: boolean;
  shouldUpsertSubmission: boolean;
}

export const UnderwritingQuoteList = ({
  submissionUuid,
  submissionType,
}: IUnderwritingQuoteGridProps) => {
  const { quotes: originalQuotes, showObsoleteQuotes } =
    useQuoteListStateContext();

  const [showEmptyQuoteMessage, setShowEmptyQuoteMessage] = useState(false);
  const {
    info: { allowCreatingQuotes },
  } = useUnderwritingSubmissionStateContext();

  const { quotes, lineItemGroups } = useMemo(() => {
    const quotes = !showObsoleteQuotes
      ? originalQuotes.filter((q) => !q.isObsolete)
      : originalQuotes;

    const lineItemGroups = combineQuoteLineItemsIntoGroups(
      submissionType,
      quotes
    );

    return {
      quotes,
      lineItemGroups,
    };
  }, [originalQuotes, showObsoleteQuotes, submissionType]);

  useEffect(() => {
    if (quotes.length === 0 && submissionType === SubmissionType.Primary) {
      setShowEmptyQuoteMessage(true);
    }
  }, [quotes, submissionType]);

  if (
    quotes.length === 0 &&
    allowCreatingQuotes &&
    submissionType !== SubmissionType.Primary
  ) {
    return (
      <ErrorPageLayout
        color={CustomPaletteColor.Primary}
        icon={false}
        message="Create first quote option"
        messageId="underwriting.quotes.noResults"
      >
        <UnderwritingQuoteCreateButton show />
      </ErrorPageLayout>
    );
  } else if (quotes.length === 0 && showEmptyQuoteMessage) {
    return (
      <ErrorPageLayout
        message="There are no quotes for this submission."
        messageId="underwriting.quotes.noResults"
      >
        <PageTitle>
          Go to the Overview or Questionnaire section to begin a new quote.
        </PageTitle>
      </ErrorPageLayout>
    );
  }

  return (
    <TableContainer className="quotes-main-body" component={PaperLayout}>
      <Table aria-label="simple table" className="quotes-wrapper" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className="quote-cell-sticky first-column"></TableCell>
            {quotes.map((quote) => (
              <TableCell
                className="quote-card-header"
                colSpan={4}
                key={quote.uuid}
              >
                <UnderwritingQuoteListDetailHeader
                  quote={quote}
                  key={quote.uuid}
                  submissionUuid={submissionUuid}
                  submissionType={submissionType}
                />
              </TableCell>
            ))}
          </TableRow>
          <CoverageQuoteGridHeaderColumnHeaderRow
            submissionType={submissionType}
            quotes={quotes}
          />
        </TableHead>
        <TableBody>
          {lineItemGroups.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="typography-container">
                <Typography className="typography-style" variant="body1">
                  {submissionType === SubmissionType.Primary
                    ? 'No coverages, endorsements, or subjectivities found'
                    : 'No layer participants found'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {lineItemGroups.map((group, index) => (
            <UnderwritingQuoteListTableBody
              quotes={quotes}
              lineItemGroup={group}
              key={index}
              submissionType={submissionType}
            />
          ))}
        </TableBody>
      </Table>
      <UnderwritingSortQuoteOptions submissionUuid={submissionUuid} />
    </TableContainer>
  );
};

interface IUnderwritingQuoteListTableBodyProps {
  quotes: QuoteDetails[];
  lineItemGroup: IQuoteLineItemGroup<QuoteLineItem>;
  submissionType: SubmissionType;
}

const UnderwritingQuoteListTableBody = ({
  quotes,
  lineItemGroup,
  submissionType,
}: IUnderwritingQuoteListTableBodyProps) => {
  return (
    <Fragment>
      <TableRow className="quote-list-coverage-header">
        <TableCell className="quote-border-right quote-cell-sticky first-cell-coverage-header first-column-header-sticky  ">
          <ConvergeTooltip title={lineItemGroup.name}>
            <div className="coverage-style">{lineItemGroup.name}</div>
          </ConvergeTooltip>
        </TableCell>
        {quotes.map((quote, index) => {
          const layerLimit = lineItemGroup.layerLimits[index];
          const layerAttachmentPoint =
            lineItemGroup.layerAttachmentPoints[index];
          const layerPremium = lineItemGroup.layerPremiums[index];
          return (
            <Fragment key={index}>
              {submissionType === SubmissionType.Primary && (
                <TableCell
                  colSpan={submissionType === SubmissionType.Primary ? 4 : 1}
                  className={`quote-border-right-2 ${
                    quote.isObsolete ? 'faded' : ''
                  }`}
                ></TableCell>
              )}
              {submissionType === SubmissionType.Excess && (
                <>
                  <TableCell />
                  <TableCell className="excess-header-font-style">
                    {typeof layerLimit === 'number'
                      ? formatDollarAmount(layerLimit)
                      : 'N/A'}
                  </TableCell>
                  <TableCell className="excess-header-font-style">
                    {typeof layerAttachmentPoint === 'number'
                      ? formatDollarAmount(layerAttachmentPoint)
                      : 'N/A'}
                  </TableCell>
                  <TableCell className="excess-header-font-style">
                    {typeof layerPremium === 'number'
                      ? formatDollarAmount(layerPremium)
                      : 'N/A'}
                  </TableCell>
                </>
              )}
            </Fragment>
          );
        })}
      </TableRow>
      {flattenQuoteLineItems(submissionType, lineItemGroup.items).map(
        (lineItem, index) => {
          return (
            <TableRow key={index} sx={{ height: 60 }}>
              <LeftCell
                isChildItem={
                  'isChildItem' in lineItem && !!lineItem.isChildItem
                }
              >
                {lineItem.name}
              </LeftCell>
              {quotes.map((quote, index) => (
                <UnderwritingQuoteListDetail
                  submissionType={submissionType}
                  quote={quote}
                  lineItem={lineItem}
                  key={index}
                />
              ))}
            </TableRow>
          );
        }
      )}
    </Fragment>
  );
};

const LeftCell = ({
  children,
  isChildItem,
}: {
  children: string;
  isChildItem: boolean;
}) => {
  return (
    <TableCell className="quote-border-right quote-cell-sticky  first-cell-coverage-body">
      <ConvergeTooltip title={children}>
        <div
          className={`'coverage-style' ${
            isChildItem ? 'subCoverage-font-style' : 'coverage-font-style '
          } `}
        >
          {children}
        </div>
      </ConvergeTooltip>
    </TableCell>
  );
};

interface CoverageQuoteGridParams {
  quote: QuoteDetails;
  submissionUuid: string;
  submissionType: SubmissionType;
  handleHideDeleteModal?: () => void;
  showDeleteConfirmation?: boolean;
  key?: string;
}
const UnderwritingQuoteListDetailHeader = ({
  quote,
  submissionUuid,
  submissionType,
  key,
}: CoverageQuoteGridParams) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    info: { boundQuote },
  } = useUnderwritingSubmissionStateContext();
  const { navigate } = useUnderwritingSubmissionDetailPage();
  const { info: submissionInfo } = useUnderwritingSubmissionStateContext();
  const { notify } = useConvergeSnackBar();
  const { upsertSubmission } = useUpsertSubmissionMutation();
  const { deleteQuote, loading: deleteLoading } = useDeleteQuoteMutation({
    onError: (error) => {
      notify(`Error deleting quote option: ${error.message}`, {
        variant: 'error',
      });
    },
    onCompleted: () => {
      notify('Quote option deleted', {
        variant: 'success',
      });
    },
  });

  const handleDeleteQuote = () => {
    deleteQuote({
      variables: { quoteUuid: quote.uuid ?? '' },
      awaitRefetchQueries: true,
    });
    const { quotesLength } = submissionInfo as SubmissionInfo;
    const isPrimary = submissionInfo.submissionType === 'PRIMARY';

    const hasRenewal =
      submissionInfo?.submission && 'renewal' in submissionInfo.submission
        ? submissionInfo.submission.renewal
        : false;
    if (
      isPrimary &&
      (quotesLength === 1 || (quotesLength === 2 && hasRenewal))
    ) {
      upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid ?? '',
            status: SubmissionStatus.Evaluation,
            subStatus: SubmissionSubStatus.EvaluationEditable,
          },
        },
      });
    }
    setShowDeleteConfirmation(false);
  };
  const [isQuoteSelected, setIsQuoteSelected] = useState(false);
  return (
    <Table
      key={key}
      style={{
        width: '100%',
        opacity:
          (boundQuote && quote.status !== QuoteStatus.Bound) || quote.isObsolete
            ? 0.5
            : undefined,
        borderBottom: 0,
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell colSpan={8} className="quote-message">
            <Box style={{ display: 'flex' }}>
              <Tooltip
                style={{ display: 'flex' }}
                title={
                  quote.isObsolete && quote.obsoleteAt
                    ? `Quote was made obsolete ${displayFormatDate(
                        quote.obsoleteAt,
                        displayFormatDate.DateFormatType.USADateFormatWithTimeAt
                      )}`
                    : `Quote was last updated ${displayFormatDate(
                        quote.updatedAt || quote.createdAt,
                        displayFormatDate.DateFormatType.USADateFormatWithTimeAt
                      )}${
                        quote.isObsolete
                          ? ' and was made obsolete at an unspecified time'
                          : ''
                      }`
                }
              >
                <Typography
                  textAlign={'center'}
                  variant="caption"
                  sx={{
                    textDecoration: quote.isObsolete
                      ? 'line-through'
                      : undefined,
                  }}
                >
                  <Typography component="span" className="quote-title">
                    {quote.inForcePolicy
                      ? 'CURRENT POLICY'
                      : quote.status === QuoteStatus.Quoted
                      ? quote.status
                      : `${quote.status} QUOTE`}
                  </Typography>
                  <Typography variant="caption" className="quote-reference-id">
                    {` (${quote.referenceId})`}
                  </Typography>
                </Typography>
              </Tooltip>
              <Box component="span" className="button-wrapper-quote">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => {
                    navigate.page({
                      sectionUrl: `${UnderwritingSubmissionDetailPageSlug.Quote}/${RaterMode.EditQuote}/${quote.uuid}`,
                    });
                  }}
                >
                  {submissionInfo.allowCreatingQuotes &&
                  !quote.isObsolete &&
                  !quote.inForcePolicy ? (
                    <EditIcon />
                  ) : (
                    <Box
                      className="Visibility-btn"
                      sx={{
                        marginRight: quote.inForcePolicy ? '10px' : '1px',
                      }}
                    >
                      <VisibilityIcon />
                    </Box>
                  )}
                </IconButton>
                {!quote.inForcePolicy && (
                  <IconButton
                    className="delete-btn"
                    color="error"
                    size="small"
                    onClick={() => setShowDeleteConfirmation(true)}
                    disabled={
                      deleteLoading ||
                      quote.isObsolete ||
                      quote.status === QuoteStatus.Bound ||
                      quote.status === QuoteStatus.Quoted
                        ? true
                        : false
                    }
                  >
                    {deleteLoading ? <Sync className="spin" /> : <DeleteIcon />}
                  </IconButton>
                )}
              </Box>
            </Box>
            {isQuoteSelected === false ? (
              <ConfirmDialog
                show={showDeleteConfirmation}
                title={`Delete ${quote.name}`}
                confirmButtonText="Yes"
                cancelButtonText="Cancel"
                content={`Are you sure you want to delete the ${quote.name}?`}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteQuote}
              />
            ) : (
              <ConfirmDialog
                onlySubmitButton={false}
                show={showDeleteConfirmation}
                title={`Delete ${quote.name}`}
                confirmButtonText="Ok"
                content={`Please unselect the ${quote.name} before deleting.`}
                onConfirm={() => setShowDeleteConfirmation(false)}
              />
            )}
          </TableCell>
        </TableRow>
        <CoverageQuoteGridHeaderTitleRow
          quote={quote}
          submissionUuid={submissionUuid}
          submissionType={submissionType}
          handleHideDeleteModal={() => setShowDeleteConfirmation(false)}
          showDeleteConfirmation={showDeleteConfirmation}
          setIsQuoteSelected={setIsQuoteSelected}
        />
        <CoverageQuoteGridHeaderPremiumSummaryRow
          quote={quote}
          submissionUuid={submissionUuid}
          submissionType={submissionType}
        />
      </TableHead>
    </Table>
  );
};

interface IUnderwritingQuoteListDetailProps {
  quote: QuoteDetails;
  lineItem: QuoteLineItem;
  submissionType: SubmissionType;
}
const UnderwritingQuoteListDetail = ({
  quote,
  lineItem,
  submissionType,
}: IUnderwritingQuoteListDetailProps) => {
  const { lineItemForThisQuote } = useMemo(() => {
    // For subcoverages, find the quote-specific subcoverage data
    if (lineItem.type === QuoteLineItemType.SubCoverage) {
      const quoteCoverages = getPaginatedNodes(quote.coverages);

      if (lineItem.parentCoverageUuid) {
        const parentCoverage = quoteCoverages.find(
          (coverage) => coverage.uuid === lineItem.parentCoverageUuid
        );
        if (parentCoverage) {
          const subcoverages = getPaginatedNodes(parentCoverage.subCoverages);
          const matchingSubcoverage = subcoverages.find(
            (sub) => sub.uuid === lineItem.uuid
          );

          if (matchingSubcoverage) {
            return {
              lineItemForThisQuote: {
                policyType: submissionType,
                uuid: matchingSubcoverage.uuid ?? 'N/A',
                name: matchingSubcoverage.name ?? 'N/A',
                type: QuoteLineItemType.SubCoverage,
                included: matchingSubcoverage.quote?.included === true,
                limit: matchingSubcoverage.quote?.subLimit,
                retention: matchingSubcoverage.quote?.subRetention,
              } as QuoteLineItem,
              lineItemsForThisQuote: [],
            };
          }
        }
      }

      for (const coverage of quoteCoverages) {
        const subcoverages = getPaginatedNodes(coverage.subCoverages);
        const matchingSubcoverage = subcoverages.find(
          (sub) => sub.uuid === lineItem.uuid
        );

        if (matchingSubcoverage) {
          return {
            lineItemForThisQuote: {
              policyType: submissionType,
              uuid: matchingSubcoverage.uuid ?? 'N/A',
              name: matchingSubcoverage.name ?? 'N/A',
              type: QuoteLineItemType.SubCoverage,
              included: matchingSubcoverage.quote?.included === true,
              limit: matchingSubcoverage.quote?.subLimit,
              retention: matchingSubcoverage.quote?.subRetention,
            } as QuoteLineItem,
            lineItemsForThisQuote: [],
          };
        }
      }

      for (const coverage of quoteCoverages) {
        const subcoverages = getPaginatedNodes(coverage.subCoverages);
        const matchingSubcoverage = subcoverages.find(
          (sub) => sub.name === lineItem.name
        );

        if (matchingSubcoverage) {
          return {
            lineItemForThisQuote: {
              policyType: submissionType,
              uuid: matchingSubcoverage.uuid ?? 'N/A',
              name: matchingSubcoverage.name ?? 'N/A',
              type: QuoteLineItemType.SubCoverage,
              included: matchingSubcoverage.quote?.included === true,
              limit: matchingSubcoverage.quote?.subLimit,
              retention: matchingSubcoverage.quote?.subRetention,
            } as QuoteLineItem,
            lineItemsForThisQuote: [],
          };
        }
      }

      // If subcoverage not found in this quote, return not included
      return {
        lineItemForThisQuote: {
          policyType: submissionType,
          uuid: lineItem.uuid,
          name: lineItem.name,
          type: QuoteLineItemType.SubCoverage,
          included: false,
          limit: null,
          retention: null,
        } as QuoteLineItem,
        lineItemsForThisQuote: [],
      };
    }

    // For regular coverages and other types, use the existing logic
    const lineItemsForThisQuote = flattenQuoteLineItems(
      submissionType,
      combineQuoteLineItems(submissionType, [quote])
    );

    const lineItemForThisQuote = lineItemsForThisQuote.find(
      (item) => item.uuid === lineItem.uuid
    );

    return {
      lineItemForThisQuote,
      lineItemsForThisQuote,
    };
  }, [
    lineItem.type,
    lineItem.parentCoverageUuid,
    lineItem.uuid,
    lineItem.name,
    submissionType,
    quote,
  ]);

  if (
    lineItemForThisQuote?.policyType === SubmissionType.Excess ||
    lineItem.policyType === SubmissionType.Excess
  ) {
    const excessLineItem = lineItemForThisQuote as IExcessQuoteLineItem;

    if (!excessLineItem) {
      return (
        <>
          <TableCell
            colSpan={4}
            className="quote-border-right text-align-table-cell"
          >
            <StatusBadge
              label={'Not participating'}
              color={CustomPaletteColor.DarkGray}
            />
          </TableCell>
        </>
      );
    }
    // if (lineItem.policyType === SubmissionType.Excess) {
    return (
      <>
        {/* <LeftCell isChildItem={false}>{lineItem.name}</LeftCell> */}
        <TableCell className="text-align-table-cell">
          {excessLineItem.policyNumber?.trim() || (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
        <TableCell className="text-align-table-cell">
          {excessLineItem.limit ? (
            formatDollarAmount(excessLineItem.limit)
          ) : (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
        <TableCell className="text-align-table-cell">
          {excessLineItem.limit ? (
            formatDollarAmount(excessLineItem.attachmentPoint)
          ) : (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
        <TableCell className="quote-border-right text-align-table-cell">
          {excessLineItem.limit ? (
            formatDollarAmount(excessLineItem.boundPremium)
          ) : (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
      </>
    );
    // }
    // return ()
    // return (
    //   <TableRow>
    //     <TableCell>{lineItemForThisQuote?.name}</TableCell>
    //   </TableRow>
    // );
  }

  return lineItemForThisQuote?.included ? (
    <CoverageQuoteGridRow
      fusionScore={lineItemForThisQuote.fusionScore}
      limit={lineItemForThisQuote.limit}
      retention={lineItemForThisQuote.retention}
      modifiedCoveragePremium={lineItemForThisQuote.modifiedCoveragePremium}
      isObsolete={quote.isObsolete}
      type={lineItemForThisQuote.type}
      nonPremiumBearing={lineItemForThisQuote?.nonPremiumBearing}
    />
  ) : (
    <CoverageQuoteGridEmptyRow
      label={toTitleCase(lineItem.type)}
      action={lineItemForThisQuote ? 'included' : 'added'}
      isObsolete={quote.isObsolete ?? false}
    />
  );
};

export const CoverageQuoteGridHeaderTitleRow = ({
  quote,
  submissionUuid,
  setIsQuoteSelected,
}: CoverageQuoteGridParams & {
  setIsQuoteSelected: Dispatch<SetStateAction<boolean>>;
}) => {
  const { notify, closeSnackbar } = useConvergeSnackBar();

  const dispatch = useQuoteListDispatchContext();
  const {
    info: submissionInfo,
    info: { submissionType },
  } = useUnderwritingSubmissionStateContext();

  const { selectQuote, unselectQuote, toggleBindConfirmDialog } =
    useQuoteListActionsContext();
  const { selectedQuotesUuids } = useQuoteListStateContext();
  const {
    bindQuote: unBindQuote,
    loading: unbindAndDraftQuoteIsLoading,
    error: bindQuoteError,
  } = useBindQuoteMutation();

  const [showCloneQuoteConfirmation, setShowCloneQuoteConfirmation] =
    useState(false);

  const [showUnBindDraftConfirmDialog, setShowUnBindDraftConfirmDialog] =
    useState(false);

  const { upsertQuote } = useUpsertQuoteMutation();

  const { cloneQuote, loading: cloneQuoteLoading } = useCloneQuoteMutation({
    onError: (error) => {
      notify(`Error duplicating quote option: ${error.message}`, {
        variant: 'error',
      });
    },
    onCompleted: () => {
      notify('Quote option cloned successfully', {
        variant: 'success',
      });
    },
  });

  const handleCloneQuote = () => {
    cloneQuote({
      variables: {
        quoteUuid: quote.uuid ?? '',
        submissionUuid: submissionUuid,
        isRenewal: false,
      },
      awaitRefetchQueries: true,
    });
    setShowCloneQuoteConfirmation(false);
  };

  const handleBind = () => {
    setShowUnBindDraftConfirmDialog(false);
    if (quote.status !== QuoteStatus.Bound && !quoteShouldBeDraft) {
      throw new Error(
        'Binding a quote should be handled with a different mutation'
      );
    }
    unBindQuote({
      variables: {
        boundQuoteUuid: quote.uuid,
        boundQuoteStatus: quoteShouldBeDraft
          ? QuoteStatus.Draft
          : QuoteStatus.Quoted,
        submissionDetails: {
          uuid: submissionUuid,
        },
        coverages: [],
        endorsements: [],
      },
      awaitRefetchQueries: true,
      update: (cache, { data: updatedData }) => {
        if (updatedData?.bindQuote?.uuid) {
          const data = cache.readQuery({
            query: GET_QUOTES,
          });
          cache.writeQuery({
            query: GET_QUOTES,
            data: {
              ...data,
              quotes: {
                ...data?.quotes,
                edges: {
                  ...data?.quotes.edges,
                  ...data?.quotes.edges.map((q) => {
                    if (q?.node?.uuid === quote.uuid) {
                      return {
                        ...q,
                        node: {
                          ...q?.node,
                          ...updatedData?.bindQuote,
                        },
                      };
                    }
                    return q;
                  }),
                },
              },
            },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (bindQuoteError) {
      notify(`Error unbinding quote: ${bindQuoteError.message}`, {
        variant: 'error',
      });
    }
  }, [bindQuoteError, notify, quote.status]);

  const quoteShouldBeDraft = useMemo(() => {
    return (
      quote.status &&
      submissionInfo.submissionStatus === SubmissionStatus.Evaluation &&
      [QuoteStatus.Bound, QuoteStatus.Quoted].includes(quote.status)
    );
  }, [quote.status, submissionInfo.submissionStatus]);

  const isSelected = useMemo(() => {
    return (
      (quote.status &&
        [QuoteStatus.Quoted, QuoteStatus.Bound].includes(quote.status)) ||
      selectedQuotesUuids.includes(quote.uuid)
    );
  }, [quote.status, quote.uuid, selectedQuotesUuids]);

  const showBindButton =
    submissionInfo.allowBindingQuote &&
    (quote.status === QuoteStatus.Quoted || quote.status === QuoteStatus.Bound)
      ? true
      : submissionInfo.allowUnbindQuote
      ? quote.status === QuoteStatus.Bound
      : quoteShouldBeDraft
      ? true
      : false;
  const validationErrors = useMemo(() => {
    return getQuoteValidationErrors(quote, submissionType, isSelected);
  }, [isSelected, quote, submissionType]);

  const [validationErrorTargetElement, setValidationErrorTargetElement] =
    useState<HTMLDivElement | null>(null);

  return (
    <TableRow
      sx={{
        height: quote.inForcePolicy ? '63px' : '0px',
      }}
    >
      <TableCell
        colSpan={2}
        align="left"
        width="50%"
        className="checkbox-container"
      >
        <Stack flexDirection={'row'} alignItems="left" columnGap="15px">
          <Checkbox
            sx={{
              opacity: quote.inForcePolicy ? 0 : 1,
              display: quote.inForcePolicy ? 'none' : 'auto',
              padding: '0px',
              color: '#666666',
              '&.Mui-checked': {
                color: '#28C0A7', // Color when checked
                backgroundColor: 'transparent',
                '& .MuiSvgIcon-root': {
                  // Styles for the checkmark icon
                  color: '#28C0A7',
                },
              },
            }}
            color={validationErrors.length > 0 ? 'error' : undefined}
            disabled={
              !submissionInfo.allowSelectingQuotesForQuotePackage ||
              quote.isObsolete ||
              quote.inForcePolicy
                ? true
                : quote.status
                ? [QuoteStatus.Quoted, QuoteStatus.Bound].includes(quote.status)
                : false
            }
            checked={isSelected}
            onChange={() => {
              if (selectedQuotesUuids.includes(quote.uuid)) {
                dispatch(unselectQuote(quote.uuid));
                setIsQuoteSelected(false);
              } else {
                dispatch(selectQuote(quote.uuid));
                setIsQuoteSelected(true);
              }
            }}
          />
          <RaterQuoteName
            quoteName={quote.name as string}
            disabled={
              !submissionInfo.allowSelectingQuotesForQuotePackage ||
              quote.isObsolete ||
              quote.inForcePolicy
                ? true
                : quote.status
                ? [QuoteStatus.Quoted, QuoteStatus.Bound].includes(quote.status)
                : false
            }
            onChange={async (quoteName) => {
              if (quoteName !== quote.name) {
                const notification = notify('Saving quote...', {
                  variant: 'info',
                });
                try {
                  await upsertQuote({
                    variables: underwritingQuoteToUpsertVariables({
                      quote: { ...quote, name: quoteName },
                      submissionUuid: submissionUuid,
                    }),
                    awaitRefetchQueries: true,
                    update: (cache, { data: updatedData }) => {
                      if (updatedData && !updatedData?.upsertQuote) {
                        throw new Error(
                          `Failed to save quote name: no quote returned by the API.`
                        );
                      }
                      if (updatedData?.upsertQuote?.uuid) {
                        const data = cache.readQuery({
                          query: GET_QUOTES,
                        });

                        cache.writeQuery({
                          query: GET_QUOTES,
                          data: {
                            ...data,
                            quotes: {
                              ...data?.quotes,
                              edges: {
                                ...data?.quotes.edges,
                                ...data?.quotes.edges.map((q) => {
                                  if (q?.node?.uuid === quote.uuid) {
                                    return {
                                      ...q,
                                      node: {
                                        ...q.node,
                                        name: quoteName,
                                      },
                                    };
                                  }
                                  return quote;
                                }),
                              },
                            },
                          },
                        });
                      }
                    },
                  });
                  closeSnackbar(notification);
                  notify(`Quote name saved.`, {
                    variant: 'success',
                  });
                } catch (error) {
                  closeSnackbar(notification);
                  notify(
                    `Failed to save quote name: ${
                      error instanceof Error ? error.message : 'unknown error'
                    }`,
                    {
                      variant: 'error',
                    }
                  );
                }
              }
            }}
            component={PrimaryTitle}
          />
        </Stack>
      </TableCell>
      <TableCell
        className="bind-quote-wrapper"
        colSpan={1}
        align="left"
        width={
          quoteShouldBeDraft && !unbindAndDraftQuoteIsLoading ? '20%' : '1%'
        }
      >
        <Stack
          flexDirection={'row'}
          justifyContent="center"
          alignItems="center"
          columnGap="5px"
          className={`table-loading-button ${
            unbindAndDraftQuoteIsLoading ? 'loading' : ''
          }`}
        >
          {validationErrors.length > 0 && (
            <>
              {/* <ConvergeTooltip
                title={
                  validationErrors.length === 1
                    ? 'There is a problem with this quote'
                    : `There are ${pluralize(
                        'problem',
                        validationErrors.length,
                        true
                      )} with this quote`
                }
              > */}
              <Box
                onMouseOver={(event) => {
                  setValidationErrorTargetElement(event.currentTarget);
                }}
                onMouseLeave={() => {
                  setValidationErrorTargetElement(null);
                }}
              >
                {/* <IconButton size="small"> */}
                <Warning color="error" />
                {/* </IconButton> */}
              </Box>
              {/* </ConvergeTooltip> */}

              <Popover
                open={Boolean(validationErrorTargetElement)}
                anchorEl={validationErrorTargetElement}
                onClose={() => {
                  setValidationErrorTargetElement(null);
                }}
                disableRestoreFocus
                sx={{
                  pointerEvents: 'none',
                }}
                anchorOrigin={{
                  vertical: -10,
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <Box className="quote-message-status-validation-container">
                    <Typography className="typography-style">
                      This quote cannot be{' '}
                      {quote.status === QuoteStatus.Quoted
                        ? 'bound'
                        : quote.status === QuoteStatus.Draft
                        ? 'quoted'
                        : quote.status === QuoteStatus.Bound
                        ? 'changed'
                        : 'modified'}{' '}
                      because of {validationErrors.length} validation error
                      {validationErrors.length > 1 ? 's' : ''}:
                    </Typography>
                  </Box>
                  <Typography component="ul">
                    {validationErrors.map((error, index) => (
                      <Typography
                        component="li"
                        key={index}
                        sx={{
                          marginLeft: 1,
                        }}
                      >
                        {error}
                      </Typography>
                    ))}
                  </Typography>
                </Box>
              </Popover>
            </>
          )}
          {showBindButton && (
            <TableActionButton
              loading={unbindAndDraftQuoteIsLoading}
              // type={
              //   quote.status === QuoteStatus.Bound
              //     ? ActionButtonType.Clear
              //     : ActionButtonType.Success
              // }
              label={
                quoteShouldBeDraft && !unbindAndDraftQuoteIsLoading
                  ? 'MARK AS DRAFT'
                  : quote.status === QuoteStatus.Bound
                  ? 'UNBIND'
                  : 'BIND'
              }
              sx={{
                textTransform: 'none',
                fontSize: '12px',
              }}
              disabled={
                unbindAndDraftQuoteIsLoading ||
                validationErrors ||
                quoteShouldBeDraft ||
                submissionInfo.allowBindingQuote ||
                submissionInfo.allowUnbindQuote
                  ? false
                  : true
              }
              onClick={() => {
                if (quote.status === QuoteStatus.Bound || quoteShouldBeDraft) {
                  setShowUnBindDraftConfirmDialog(true);
                } else {
                  dispatch(toggleBindConfirmDialog(true, quote.uuid));
                }
              }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell
        className="clone-quote-wrapper"
        colSpan={0}
        align="right"
        width={submissionInfo.allowBindingQuote && showBindButton ? '8%' : '4%'}
      >
        <Stack
          flexDirection={'row'}
          justifyContent="center"
          alignItems="center"
          columnGap="5px"
        >
          {!quote?.inForcePolicy && (
            <NewConvergeButton
              label="CLONE"
              size="small"
              loading={cloneQuoteLoading}
              onClick={() => setShowCloneQuoteConfirmation(true)}
              disabled={quote.isObsolete ? true : false}
            />
          )}

          <ConfirmDialog
            show={showCloneQuoteConfirmation}
            title={`Clone ${quote.name}`}
            confirmButtonText="Yes"
            cancelButtonText="No"
            content={`Are you sure you want to clone ${quote.name}?`}
            onCancel={() => setShowCloneQuoteConfirmation(false)}
            onConfirm={handleCloneQuote}
          />
        </Stack>
        <ConfirmDialog
          show={showUnBindDraftConfirmDialog}
          title={`${quoteShouldBeDraft ? 'Draft' : 'Unbind'} quote: ${
            quote.name
          }`}
          confirmButtonText="Yes"
          cancelButtonText="No"
          content={`Are you sure you want to ${
            quoteShouldBeDraft ? 'draft' : 'unbind'
          } this quote?`}
          onCancel={() => setShowUnBindDraftConfirmDialog(false)}
          onConfirm={handleBind}
        />
      </TableCell>
    </TableRow>
  );
};

export const CoverageQuoteGridHeaderPremiumSummaryRow = ({
  quote,
  submissionType,
}: CoverageQuoteGridParams & {
  submissionType: SubmissionType;
}) => {
  const { t } = useTranslation();

  const limit =
    submissionType === SubmissionType.Primary
      ? quote.limit
      : quote.excessConvergeLimit;

  const retentionOrAttachmentPoint =
    submissionType === SubmissionType.Primary
      ? quote.retention
      : quote.excessConvergeAttachmentPoint;

  const premium =
    submissionType === SubmissionType.Primary
      ? quote.selectedQuotedPremium ?? quote.quotedPremium
      : quote.excessConvergeQuotedPremium;

  return (
    <TableRow className="header-amount">
      <TableCell colSpan={4}>
        <div className="quotes-flex-wrapper">
          <div>
            <span>
              {t('underwriting.quote.grid.summary.aggregateLimit', 'Limit')}
            </span>
            <PrimaryTitle>
              {typeof limit === 'number' ? formatDollarAmount(limit) : 'N/A'}
            </PrimaryTitle>
          </div>
          <div>
            <span>
              {submissionType === SubmissionType.Primary
                ? 'Retention'
                : 'Attachment point'}
            </span>
            <PrimaryTitle
              color={
                (typeof retentionOrAttachmentPoint === 'number'
                  ? 'primary'
                  : 'darkGray') as CustomPaletteColorType
              }
            >
              {typeof retentionOrAttachmentPoint === 'number'
                ? formatDollarAmount(retentionOrAttachmentPoint)
                : 'N/A'}
            </PrimaryTitle>
          </div>
          <div className="right-span">
            <span>
              {t('underwriting.quote.grid.summary.premium', 'Premium')}
            </span>
            <PrimaryTitle
              color={
                (typeof premium === 'number'
                  ? 'primary'
                  : 'darkGray') as CustomPaletteColorType
              }
            >
              {typeof premium === 'number'
                ? formatDollarAmount(premium)
                : 'N/A'}
            </PrimaryTitle>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

const CoverageQuoteGridHeaderColumnHeaderRow = ({
  quotes,
  submissionType,
}: {
  quotes: QuoteDetails[];
  submissionType: SubmissionType;
}) => {
  const { t } = useTranslation();

  return (
    <TableRow className="header-column">
      <TableCell
        className="quote-cell-sticky quote-header-cell-sticky"
        sx={{ backgroundColor: '#ffffff' }}
      ></TableCell>
      {quotes.map((quote, index) => (
        <Fragment key={index}>
          {submissionType === SubmissionType.Primary ? (
            <>
              <CoverageQuoteGridRowCell
                width="100px"
                align="center"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
              >
                {t(
                  'underwriting.quote.grid.header.fusionScore',
                  'Fusion Score'
                )}
              </CoverageQuoteGridRowCell>
              <CoverageQuoteGridRowCell
                width="90px"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
                textAlign="center"
              >
                {t('underwriting.quote.grid.header.limit', 'Limit')}
              </CoverageQuoteGridRowCell>
              <CoverageQuoteGridRowCell
                width="90px"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
                textAlign="center"
              >
                {t('underwriting.quote.grid.header.retention', 'Retention')}
              </CoverageQuoteGridRowCell>
              <CoverageQuoteGridRowCell
                width="100px"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-right`}
              >
                {t('underwriting.quote.grid.header.modifiedPremium', 'Premium')}
              </CoverageQuoteGridRowCell>
            </>
          ) : (
            <>
              <CoverageQuoteGridRowCell
                width="100px"
                align="center"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
              >
                Policy number
              </CoverageQuoteGridRowCell>

              <CoverageQuoteGridRowCell
                width="100px"
                align="center"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
              >
                Limit
              </CoverageQuoteGridRowCell>

              <CoverageQuoteGridRowCell
                width="100px"
                align="center"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
              >
                Attachment point
              </CoverageQuoteGridRowCell>

              <CoverageQuoteGridRowCell
                width="100px"
                align="center"
                className={`${
                  quote.isObsolete ? 'faded-font-color' : ''
                } quote-border-left`}
              >
                Premium
              </CoverageQuoteGridRowCell>
            </>
          )}
        </Fragment>
      ))}
    </TableRow>
  );
};

const CoverageQuoteGridRow = ({
  fusionScore,
  limit,
  modifiedCoveragePremium,
  retention,
  isObsolete,
  type,
  nonPremiumBearing,
}: {
  fusionScore?: number | null;
  limit?: number | null;
  retention?: number | null;
  modifiedCoveragePremium?: number | null;
  isObsolete?: null | boolean;
  type?: QuoteLineItemType;
  nonPremiumBearing?: boolean | null;
}) => {
  if (type === QuoteLineItemType.Subjectivity) {
    return (
      <TableCell
        colSpan={4}
        align="center"
        className={`${
          isObsolete ? 'faded' : ''
        } quote-border-right quote-border-left`}
      >
        <StatusBadge label={'Included'} color={CustomPaletteColor.Primary} />
      </TableCell>
    );
  }
  if (type === QuoteLineItemType.Endorsement && nonPremiumBearing) {
    return (
      <TableCell
        colSpan={4}
        align="center"
        className="quote-border-right quote-border-left"
      >
        <StatusBadge
          label="Non-premium bearing"
          color={CustomPaletteColor.Primary}
        />
      </TableCell>
    );
  }

  return (
    <>
      <CoverageQuoteGridRowCell
        width="90px"
        align="center"
        className={`${isObsolete ? 'faded' : ''} quote-border-left`}
      >
        {fusionScore && displayNumber(fusionScore)}
      </CoverageQuoteGridRowCell>
      <CoverageQuoteGridRowCell
        width="100px"
        className={isObsolete ? 'faded' : ''}
      >
        {formatDollarAmount(limit)}
      </CoverageQuoteGridRowCell>
      <CoverageQuoteGridRowCell
        width="100px"
        className={isObsolete ? 'faded' : ''}
      >
        {formatDollarAmount(retention)}
      </CoverageQuoteGridRowCell>
      <CoverageQuoteGridRowCell
        width="120px"
        className={`${isObsolete ? 'faded' : ''} quote-border-right`}
      >
        <b>{formatDollarAmount(modifiedCoveragePremium)}</b>
      </CoverageQuoteGridRowCell>
    </>
  );
};

interface CoverageQuoteGridRowCellProps {
  width: string;
  align?: 'left' | 'right' | 'center';
  textAlign?: 'left' | 'right' | 'center';
  children: ReactNode;
  className?: string;
}

const CoverageQuoteGridRowCell = ({
  width,
  align = 'right',
  children,
  className,
  textAlign,
}: CoverageQuoteGridRowCellProps) => {
  return (
    <TableCell
      sx={{
        minWidth: width,
        maxWidth: width,
        textAlign,
      }}
      align={align}
      className={`${className} quotes-cell`}
    >
      <Box className="text-align-table-cell">{children}</Box>
    </TableCell>
  );
};

interface ICoverageQuoteGridEmptyRowProps {
  label: string;
  action: string;
  isObsolete: boolean | null;
}
const CoverageQuoteGridEmptyRow = ({
  label = 'Coverage',
  action = 'included',
  isObsolete,
}: ICoverageQuoteGridEmptyRowProps) => {
  return (
    <TableCell
      colSpan={4}
      align="center"
      className={`${
        isObsolete ? 'faded' : ''
      } quote-border-right quote-border-left`}
    >
      <StatusBadge
        label={`${label} not ${action}`}
        color={CustomPaletteColor.DarkGray}
      />
    </TableCell>
  );
};

export function getQuoteValidationErrors(
  quote: QuoteDetails,
  submissionType: SubmissionType | null | undefined,
  isSelected: boolean
) {
  const validationErrors: string[] = [];

  if (!quote.status) {
    validationErrors.push('Quote has no status');
  }

  if (submissionType === SubmissionType.Excess && isSelected) {
    const hasConvergeAsParticipant = quote?.excessTowerLayers?.edges?.some(
      ({ node: layer }) =>
        layer.participants?.edges?.some(({ node: participant }) =>
          isConverge(participant)
        )
    );

    if (!hasConvergeAsParticipant) {
      validationErrors.push('Excess quote must have Converge as a participant');
    }
  }

  return validationErrors;
}
